/* eslint react-hooks/preserve-manual-memoization: ["warn"] */
/* eslint react-hooks/immutability: ["warn"] */
import {
  InputPopup,
  InputPopupProvider,
  useInputPopup,
} from "@coworker/app/src/components/InputPopup";
import { WorkspacesProvider } from "@coworker/app/src/hooks/useWorkspaces";
import { TooltipProvider } from "@coworker/app/src/components/Tooltip";
import {
  ToastNotification,
  ToastNotificationProvider,
} from "@coworker/components/src/components/ToastNotification";
import { ChangeTeamWrapper } from "@coworker/app/src/components/ChangeTeamWrapper";
import { SelectedTasksContext } from "@coworker/app/src/components/SelectedTasksProvider";
import { Wrapper } from "@coworker/app/src/components/Layout/styles";
import { useMultiSelect } from "@coworker/app/src/hooks/useMultiSelect";
import {
  FLAGS,
  useUserPermissionsQuery,
} from "@coworker/app/src/hooks/useFlag";
import {
  useCountryId,
  useHashedUid,
  useLoggedInUserState,
  useStartedAtVersion,
  useStoreId,
  useUID,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import {
  setCountry,
  setStartedAtVersion,
  setUserId,
  setUserStore,
} from "@coworker/app/src/helpers/tracker";
import versionJson from "@coworker/app/src/core/version.json";
import { useUpdateUserLastActiveAt } from "@coworker/apprestructured/src/shared/hooks/users/useUpdateUserLastActiveAt";
import { INTERVAL } from "@coworker/app/src/constants/clientTime";
import { useSetInitialLanguage } from "@coworker/app/src/components/LanguageSetter";
import React from "react";
import { Outlet } from "react-router";
import ModalContextProvider from "@coworker/apprestructured/src/layout/context/ModalContextProvider";
import MainContentScrollDirectionContextProvider from "@coworker/apprestructured/src/layout/context/MainContentScrollDirectionContextProvider";
import TaskDetailSheet from "@coworker/apprestructured/src/home/components/TasksPreview/TasksDetailSheet";
import TaskViewerSheet from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/TaskViewerSheet";
import { FixaWindow } from "@coworker/reusable/helpers/devices";
import TaskCreatorSheet from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/TaskCreatorSheet/TaskCreatorSheet";
import TaskEditorSheet from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/TaskEditorSheet/TaskEditorSheet";
import SaveGuardProvider from "@coworker/apprestructured/src/save-guard/SaveGuardProvider/SaveGuardProvider";
import { usePendo } from "@coworker/apprestructured/src/layout/hooks/usePendo";

function BrowserBackInputPopupWatcher() {
  const { closePopup } = useInputPopup();
  React.useEffect(() => {
    function listen() {
      closePopup();
    }

    window.addEventListener("popstate", listen);
    return () => {
      window.removeEventListener("popstate", listen);
    };
  }, [closePopup]);
  return null;
}

function useSetAnalyticsUserProperties() {
  const uid = useUID();
  const country_id = useCountryId();
  const store_id = useStoreId();
  const hashed_uid = useHashedUid();
  const started_at_version = useStartedAtVersion();

  React.useEffect(() => {
    if (store_id) {
      setUserStore(store_id);
      const version = started_at_version || versionJson.version;
      setStartedAtVersion(version);
    }

    if (uid) {
      setUserId(uid, hashed_uid);
    }

    if (country_id) {
      setCountry(country_id);
    }
  }, [store_id, started_at_version, uid, country_id, hashed_uid]);
}

function useMarkUserActive() {
  const loggedInUser = useLoggedInUserState();
  const updateUserLastActiveAtMutation = useUpdateUserLastActiveAt(
    loggedInUser.uid
  );

  const lastUpdatedRef = React.useRef<string | null>(null);
  const lastCallTimeRef = React.useRef(0);

  const throttledUpdate = React.useCallback(() => {
    const now = Date.now();
    const THROTTLE_TIME = INTERVAL.HOUR * 2; // let's 2 hours to avoid too maby calls to backend

    if (
      lastUpdatedRef.current === null ||
      now - lastCallTimeRef.current >= THROTTLE_TIME
    ) {
      lastCallTimeRef.current = now;
      updateUserLastActiveAtMutation.mutate();
      lastUpdatedRef.current = loggedInUser.lastActiveAt;
    }
  }, [updateUserLastActiveAtMutation, loggedInUser?.lastActiveAt]);

  React.useEffect(() => {
    if (
      !loggedInUser?.lastActiveAt ||
      updateUserLastActiveAtMutation?.status === "pending"
    )
      return;

    const lastActiveAtDate = new Date(loggedInUser.lastActiveAt);
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    if (lastUpdatedRef.current === null) {
      throttledUpdate();
    }

    const lastUpdatedDate = new Date(lastUpdatedRef.current as string);

    if (
      lastActiveAtDate < oneDayAgo &&
      lastActiveAtDate.getTime() !== lastUpdatedDate.getTime()
    ) {
      throttledUpdate();
    }
  }, [
    loggedInUser?.lastActiveAt,
    updateUserLastActiveAtMutation,
    throttledUpdate,
  ]);
}

function Setters() {
  useSetAnalyticsUserProperties();
  useSetInitialLanguage();
  useMarkUserActive();
  usePendo();
  return null;
}

export const AppProviders = () => {
  const multiSelectState = {
    inProgress: useMultiSelect(),
    open: useMultiSelect(),
  };
  const { data: userPermissions } = useUserPermissionsQuery();

  (window as FixaWindow).hideForGermanyUsers = userPermissions?.includes(
    FLAGS.GERMANY_ROLLOUT
  );
  (window as FixaWindow).enableDiscoverNow = userPermissions?.includes(
    FLAGS.ENABLE_DISCOVER_NOW
  );
  (window as FixaWindow).enableNewProductSearch = userPermissions?.includes(
    FLAGS.ENABLE_NEW_PRODUCT_SEARCH
  );
  (window as FixaWindow).enableNotifications = userPermissions?.includes(
    FLAGS.ENABLE_NOTIFICATIONS
  );
  (window as FixaWindow).enableTaskManagement = userPermissions?.includes(
    FLAGS.ENABLE_TASK_MANAGEMENT
  );
  (window as FixaWindow).enableTestbuyRefactor = userPermissions?.includes(
    FLAGS.ENABLE_TESTBUY_REFACTOR
  );
  (window as FixaWindow).enablePalletMultiPack = userPermissions?.includes(
    FLAGS.ENABLE_PALLET_MULTIPACK_DATA_SERVICE
  );
  (window as FixaWindow).enableMultipleHfbsForTestbuy =
    userPermissions?.includes(FLAGS.ENABLE_MULTIPLE_HFBS_FOR_TESTBUY);
  const { popupOpen } = useInputPopup();
  return (
    <SaveGuardProvider>
      <InputPopupProvider>
        <Setters />
        <InputPopupProvider>
          <WorkspacesProvider>
            <TooltipProvider>
              <ToastNotificationProvider>
                <ChangeTeamWrapper>
                  <SelectedTasksContext.Provider value={multiSelectState}>
                    <Wrapper disabled={popupOpen}>
                      <ModalContextProvider>
                        <MainContentScrollDirectionContextProvider>
                          <Outlet />
                          <TaskDetailSheet />
                          <TaskCreatorSheet />
                          <TaskViewerSheet />
                          <TaskEditorSheet />
                        </MainContentScrollDirectionContextProvider>
                      </ModalContextProvider>
                    </Wrapper>
                    <InputPopup />
                  </SelectedTasksContext.Provider>
                  <ToastNotification />
                  <BrowserBackInputPopupWatcher />
                </ChangeTeamWrapper>
              </ToastNotificationProvider>
            </TooltipProvider>
          </WorkspacesProvider>
        </InputPopupProvider>
        <InputPopup />
      </InputPopupProvider>
    </SaveGuardProvider>
  );
};
